import type { FinalUrlItem } from './api';
import type { MetaAdSet } from './metaCampaign';

// Re-export for consumers of this module
export type { FinalUrlItem };

export type PlatformSource = 'Google Ads' | 'Meta Ads';

/**
 * Union of the campaign-status vocabularies surfaced by both platforms.
 * Google emits `ENABLED | PAUSED | REMOVED`; Meta emits
 * `ACTIVE | PAUSED | ARCHIVED | DELETED`. The Campaign type is shared
 * across platforms (one type, branched by `platform_source`) so both
 * vocabularies need to be representable without casts. The shared
 * `CampaignStatusBadge` already keys color on the uppercased value
 * and matches both sets — see
 * `components/features/CampaignCard/shared/CampaignStatusBadge.tsx`.
 */
export type CampaignStatus =
  | 'ENABLED' | 'PAUSED' | 'REMOVED'  // Google
  | 'ACTIVE' | 'ARCHIVED' | 'DELETED'; // Meta

export interface Campaign {
  campaign_id: string;
  campaign_name: string;
  google_ads_account_id?: string; // The Google Ads account this campaign belongs to
  status: CampaignStatus;
  campaign_status?: string; // Legacy field name
  campaign_type?: string;
  budget: number;
  budget_type?: string;
  bidding_strategy: string;
  start_date?: string; // Campaign start date (YYYY-MM-DD format)
  end_date?: string; // Campaign end date (YYYY-MM-DD format) - if set and past, campaign is considered "Ended"
  target_density?: number; // Budget optimization target density (from saved configuration)
  // 28-day metrics
  impressions: number;
  clicks: number;
  conversions: number;
  conversions_value: number;
  cost: number;
  ctr: number;
  conversion_rate: number;
  cpc: number;
  cpa: number;
  // 7-day metrics
  impressions_7d?: number;
  clicks_7d?: number;
  conversions_7d?: number;
  conversions_value_7d?: number;
  cost_7d?: number;
  ctr_7d?: number;
  conversion_rate_7d?: number;
  cpc_7d?: number;
  cpa_7d?: number;
  created_at: string;
  updated_at: string;
  flouri_optimize_budget?: boolean;
  ads_group_list?: AdGroup[]; // For Search campaigns
  asset_group_list?: AssetGroup[]; // For Performance Max campaigns
  landing_pages?: LandingPage[]; // For landing page display
  // Campaign-level assets (loaded from app_configuration)
  sitelinks?: CampaignLevelAsset[];
  callouts?: CampaignLevelAsset[];
  structured_snippets?: StructuredSnippet[];
  // Negative keywords
  negative_keywords?: NegativeKeywordItem[];
  // Audience targeting (for Demand Gen and other campaign types)
  audience_targeting?: AudienceTargetingData;
  // Network settings (which networks this campaign targets)
  network_settings?: NetworkSettings;
  // Location targeting (countries, regions, etc.)
  location_targeting?: LocationTarget[];
  // Language targeting
  language_targeting?: LanguageTarget[];

  // ── Platform discriminator + Meta-specific fields ──────────────────────
  // Absent on legacy Google rows; defaults to `'Google Ads'` when
  // unset. The unified `app_configuration` row stamps this on every
  // entry so cross-platform readers can branch without lossy
  // translation. Meta-only fields below are populated on Meta entries
  // and absent on Google entries.
  platform_source?: PlatformSource;
  // Meta-only
  ad_sets?: MetaAdSet[];
  objective?: string | null;
  meta_ad_account_id?: string | null;
  buying_type?: string | null;
  is_cbo?: boolean;
  daily_budget?: number | null;
  lifetime_budget?: number | null;
  special_ad_categories?: string[];
  bid_strategy?: string | null;
  start_time?: string | null;
  stop_time?: string | null;
  // Meta-only aggregated extras (set by the backend constructor)
  reach?: number;
  cpm?: number;
  purchases?: number;
  purchases_value?: number;
}

/**
 * True when the campaign came from Meta Ads. Pre-A3 rows without an
 * explicit `platform_source` default to Google — Meta entries always
 * have the discriminator set by the backend constructor.
 */
export function isMetaCampaign(c: Pick<Campaign, 'platform_source'>): boolean {
  return c.platform_source === 'Meta Ads';
}

// Audience Targeting Types
export interface AudienceSegment {
  criterion_id: string;
  audience_type: string;
  audience_name?: string | null;
  user_list_id?: string | null;
  is_negative: boolean;
  status: string;
}

export interface AdGroupAudience {
  ad_group_id: string;
  ad_group_name?: string | null;
  audiences: AudienceSegment[];
}

export interface AudienceTargetingData {
  ad_group_audiences: AdGroupAudience[];
  campaign_audiences: AudienceSegment[];
  demographics: AudienceSegment[];
  total_audiences: number;
}

// Audience Metrics (for Performance Metrics tab)
export interface AudienceMetrics {
  criterion_id: string;
  audience_type: string;
  audience_name: string;
  ad_group_id: string;
  ad_group_name: string;
  impressions: number;
  clicks: number;
  cost: number;
  conversions: number;
  ctr: number;
  video_views?: number;
}

export interface AudienceMetricsResponse {
  campaign_id: string;
  campaign_name: string;
  audiences: AudienceMetrics[];
  total_audiences: number;
}

// Network Settings (which advertising networks the campaign targets)
export interface NetworkSettings {
  target_google_search?: boolean;
  target_search_network?: boolean;
  target_content_network?: boolean;
  target_partner_search_network?: boolean;
}

// Location Targeting
export interface LocationTarget {
  target_id?: string | null;
  target_name?: string | null;
  is_negative: boolean;
}

// Language Targeting
export interface LanguageTarget {
  target_id?: string | null;
  target_name?: string | null;
}

export interface NegativeKeywordItem {
  keyword_id?: string;
  keyword_text: string;
  match_type?: string;
  status?: string;
}

export interface CampaignDetail extends Campaign {
  ad_groups: AdGroup[];
  keywords: Keyword[];
  targeting_settings: TargetingSettings;
}

export interface AdGroup {
  ad_group_id: string;
  ad_group_name: string;
  ads_group_id?: string; // Legacy field name
  ads_group_name?: string; // Legacy field name
  status: 'ENABLED' | 'PAUSED' | 'REMOVED';
  max_cpc_bid: number;
  bidding_strategy_type?: string;
  ads: Ad[];
  ads_list?: Ad[]; // Alternative field name
  keywords_list?: Keyword[]; // For cold start optimization
  flouri_optimize_bids?: boolean; // For cold start optimization
  // RMF compliance: ad group level metrics (28-day aggregates)
  impressions?: number;
  clicks?: number;
  cost?: number;
  conversions?: number;
  ctr?: number;
  avg_cpc?: number;
}

export interface Ad {
  ad_id: string;
  ad_type: string;
  ad_name?: string; // Optional display name
  status: 'ENABLED' | 'PAUSED' | 'REMOVED';
  headlines: Array<string | { text: string; pinned_field?: string }>;
  descriptions: Array<string | { text: string; pinned_field?: string }>;
  final_urls: FinalUrlItem[];
  // RSA specific fields
  path1?: string;
  path2?: string;
  // RDA specific fields
  long_headline?: string;
  business_name?: string;
  rda_headlines?: string[];
  rda_descriptions?: string[];
  rda_long_headline?: string;
  rda_business_name?: string;
  rda_marketing_images?: string[];
  rda_square_marketing_images?: string[];
  rda_logo_images?: string[];
  // Demand Gen specific fields
  long_headlines?: string[];  // For Video ads
  call_to_action?: string;
  marketing_images?: string[];  // Landscape 1.91:1
  square_marketing_images?: string[];  // Square 1:1
  portrait_marketing_images?: string[];  // Portrait 4:5
  logo_images?: string[];  // Logo 1:1
  videos?: string[];  // YouTube video asset IDs (Video ads)
  carousel_cards?: Array<{  // Carousel ads
    headline: string;
    image_id?: string;  // Media Library image ID (for uploads)
    asset_id?: string;  // Google Ads asset ID (for existing cards)
    final_url?: string;
  }>;
}

export interface Keyword {
  keyword_id: string;
  keyword_text: string;
  match_type: 'EXACT' | 'PHRASE' | 'BROAD';
  max_cpc_bid: number;
  status: 'ENABLED' | 'PAUSED';
  quality_score?: number;
  final_urls?: FinalUrlItem[];
  // RMF compliance: keyword level metrics (28-day aggregates)
  impressions?: number;
  clicks?: number;
  cost?: number;
  conversions?: number;
}

export interface TargetingSettings {
  locations: Location[];
  languages: Language[];
  demographics: Demographics;
}

export interface Location {
  location_id: string;
  location_name: string;
}

export interface Language {
  language_id: string;
  language_name: string;
}

export interface Demographics {
  age_ranges: string[];
  genders: string[];
}

export interface CampaignFilters {
  search: string;
  type: string;
  status: string;
}

export interface CampaignConfig {
  campaign_id: string;
  budget: number;
  target_density: number;
  enabled: boolean;
}

export interface LandingPage {
  url: string;
  hash: string;
  authorized?: boolean;
  source_name?: string;
  source_type?: string;
  source_id?: string;
}

export interface AssetGroup {
  asset_group_id: string;
  asset_group_name: string;
  asset_group_status?: string;
  status?: 'ENABLED' | 'PAUSED';
  final_urls?: Array<{ url: string } | string>;
  final_url_list?: string[];
  asset_list?: Asset[];
  // Image assets from Google Ads
  marketing_images?: ImageAsset[];
  square_marketing_images?: ImageAsset[];
  portrait_marketing_images?: ImageAsset[];
  logo_images?: ImageAsset[];
}

export interface ImageAsset {
  asset_id: string;
  url: string;
  performance_label?: string;
}

export interface Asset {
  asset_id?: string;
  type?: 'headline' | 'description' | 'long_headline' | 'business_name' | 'image'; // New field name
  asset_type?: 'headline' | 'description' | 'long_headline' | 'business_name' | 'image'; // Legacy field name
  text?: string; // New field name
  content?: string; // Legacy field name
  asset_text?: string; // Legacy field name
  performance_label?: string;
  status?: 'ENABLED' | 'PAUSED' | 'REMOVED';
}

export interface CampaignLevelAsset {
  asset_id: string;
  text: string;
  status: 'ENABLED' | 'PAUSED' | 'REMOVED';
  final_url?: string; // For sitelinks only
}

export interface StructuredSnippet {
  asset_id: string;
  header: string;
  values: string[];
  status: 'ENABLED' | 'PAUSED' | 'REMOVED';
}